import { useCallback, useMemo } from 'react';
import {
  LessonUsageFragment,
  useClassUsageQuery,
  useDivisionUsageQuery,
  usePersonUsageQuery,
} from '../types/planung-graphql-client-defs';
import { useMemorizedCacheTags } from './useMemorizedCacheTag';

export type LessonUsageKind = 'class' | 'teacher' | 'classDivision';

export type LessonUsageResult = {
  isUsed: boolean;
  /** Zahl aller Zuordnungen; `lessons` enthält davon höchstens drei als Beispiel. */
  lessonsCount: number;
  lessons: LessonUsageFragment['lessons'];
};

const UNUSED: LessonUsageResult = { isUsed: false, lessonsCount: 0, lessons: [] };

/**
 * Sagt für die Zeilen einer Stammdaten-Tabelle, ob sie in Zuordnungen verwendet werden, und liefert
 * je Zeile höchstens drei Zuordnungen für den Hinweis „verwendet in“. Lädt dafür eine kleine Abfrage
 * je Tabelle statt aller Zuordnungen der Organisation.
 */
export const useLessonUsage = (kind: LessonUsageKind, rows: readonly { uuid: string }[] | undefined) => {
  const uuids = useMemo(() => [...new Set((rows ?? []).map((row) => row.uuid))].sort(), [rows]);
  const variables = useMemo(() => ({ uuids }), [uuids]);
  const empty = uuids.length === 0;

  // Die Antwort hängt an den Zuordnungen und ihren Versionen, nicht nur am Eintrag selbst.
  const classContext = useMemorizedCacheTags(['CLASSES', 'LESSONS', 'LESSON_CLASS', 'TIMETABLE_VERSIONS']);
  const personContext = useMemorizedCacheTags(['PERSONS', 'LESSONS', 'LESSON_CLASS', 'TIMETABLE_VERSIONS']);
  const divisionContext = useMemorizedCacheTags(['CLASS_DIVISIONS', 'LESSONS', 'LESSON_CLASS', 'TIMETABLE_VERSIONS']);

  const [{ data: classData }] = useClassUsageQuery({
    variables,
    pause: empty || kind !== 'class',
    context: classContext,
  });
  const [{ data: personData }] = usePersonUsageQuery({
    variables,
    pause: empty || kind !== 'teacher',
    context: personContext,
  });
  const [{ data: divisionData }] = useDivisionUsageQuery({
    variables,
    pause: empty || kind !== 'classDivision',
    context: divisionContext,
  });

  const usages =
    kind === 'class'
      ? classData?.classUsage
      : kind === 'teacher'
        ? personData?.personUsage
        : divisionData?.divisionUsage;

  const byUuid = useMemo(() => {
    const map = new Map<string, LessonUsageResult>();
    for (const usage of usages ?? []) {
      map.set(usage.uuid, {
        isUsed: usage.lessonsCount > 0,
        lessonsCount: usage.lessonsCount,
        lessons: usage.lessons,
      });
    }
    return map;
  }, [usages]);

  return useCallback((uuid: string): LessonUsageResult => byUuid.get(uuid) ?? UNUSED, [byUuid]);
};
